// PKGV.A2 (#793) — thin seam owned by A2, isolating PKGNeighbourhoodPanel
// from B1's route shape. B1's GET /projects/:projectId/pkg/subgraph (#795,
// merged ce85023c, including the Gate-2 row-explosion remediation) is now
// live — this function's body is unchanged from the pre-merge version
// (locked at V4 adjudication); wiring it up only required confirming, not
// changing, the shape below.
//
// Response field names (fromNodeId/toNodeId, camelCase entityType/
// entityId/summary) mirror apps/api/src/services/pkgTraversalService.ts
// SubgraphNode/SubgraphEdge exactly, re-confirmed against the merged
// remediation (its own comments note the node/edge shape is "unaffected
// by this remediation" — verified directly, not taken on faith).

import { apiFetch } from '../../lib/api';
import type { PKGNode, PKGEdge, PKGNodeEntityType, PKGEdgeType } from '@blueprint/shared';

interface SubgraphApiNode {
  id: string;
  entityType: PKGNodeEntityType;
  entityId: string;
  summary: string | null;
}

interface SubgraphApiEdge {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  edgeType: PKGEdgeType;
  weight: number;
  createdByAi: boolean;
}

interface SubgraphApiResponse {
  nodes: SubgraphApiNode[];
  edges: SubgraphApiEdge[];
  stats: {
    nodeCount: number;
    edgeCount: number;
    queryTimeMs: number;
    truncated?: boolean;
  };
}

export interface FetchedSubgraph {
  nodes: PKGNode[];
  edges: PKGEdge[];
  truncated: boolean;
}

export async function fetchSubgraph(
  projectId: string,
  anchorId: string,
  depth = 2,
): Promise<FetchedSubgraph> {
  const params = new URLSearchParams({ anchorIds: anchorId, depth: String(depth) });
  const raw = await apiFetch<SubgraphApiResponse>(
    `/projects/${projectId}/pkg/subgraph?${params.toString()}`,
  );

  return {
    nodes: raw.nodes.map(n => ({
      id: n.id,
      entityType: n.entityType,
      entityId: n.entityId,
      summary: n.summary,
    })),
    // PKGEdge uses source/target (React Flow convention); the API uses
    // fromNodeId/toNodeId (matches the pkg_edges column names) — this is
    // the one translation this seam exists to own.
    edges: raw.edges.map(e => ({
      id: e.id,
      source: e.fromNodeId,
      target: e.toNodeId,
      edgeType: e.edgeType,
      createdByAi: e.createdByAi,
    })),
    truncated: raw.stats.truncated ?? false,
  };
}
